import {
  LTV_THRESHOLD,
  MAX_LOAN_AMOUNT,
  MAX_PROPERTY_VALUE,
  MIN_LOAN_AMOUNT,
  MIN_PROPERTY_VALUE,
} from '@hex/calculators-shared/constants';
import { getHemnetQueryParams, getLTV } from '@hex/calculators-shared/utils';
import { getAmountFromUrl } from '@hex/calculators-shared/utils/getAmountFromUrl';
import { trackCalculatorInit, useTrackCalculatorLoad } from '@hex/shared/tracking';
import { useEffect, useState } from 'react';
import { type RegisterOptions, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormDataType } from '../../types';

export function useCostForm() {
  const { t } = useTranslation();
  const [ltv, setLtv] = useState<number>();
  const defaultPropertyValue = getHemnetQueryParams().pris || '';
  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    reset,
    formState: { errors, submitCount, isDirty, dirtyFields },
  } = useForm<FormDataType>({
    defaultValues: {
      propertyValue: defaultPropertyValue,
      loanAmount: getAmountFromUrl('loanAmount')?.toString() || '',
      secondaryApplicantExist: null,
      primaryApplicantIncome: '',
      secondaryApplicantIncome: '',
      partnership: '',
    },
  });

  const allFields = watch();

  const ltvHandler = (propertyValue: string, loanAmount: string) => {
    const calculatedLtv = getLTV(propertyValue, loanAmount);
    const maxValue = new Intl.NumberFormat('sv-se').format(Math.floor(parseInt(propertyValue) * (LTV_THRESHOLD / 100)));

    if (calculatedLtv && calculatedLtv > LTV_THRESHOLD) {
      setLtv(undefined);

      return t('shared:personalInformation.inputs.loanAmount.rules.ltv', { calculatedLtv, maxValue });
    }

    setLtv(calculatedLtv);

    return undefined;
  };

  const propertyValueFieldRules = {
    required: t('shared:personalInformation.inputs.propertyValue.requiredError'),
    min: {
      value: MIN_PROPERTY_VALUE,
      message: t('shared:personalInformation.inputs.propertyValue.rules.min', { amount: MIN_PROPERTY_VALUE }),
    },
    max: {
      value: MAX_PROPERTY_VALUE,
      message: t('shared:personalInformation.inputs.propertyValue.rules.max', { amount: MAX_PROPERTY_VALUE + 1 }),
    },
    onBlur: () => {
      const message = ltvHandler(allFields.propertyValue, allFields.loanAmount);
      submitCount === 0 && (message ? setError('loanAmount', { message }) : clearErrors('loanAmount'));
    },
    deps: 'loanAmount',
  };

  type LoanAmountFieldRules = Omit<RegisterOptions, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;

  const loanAmountFieldRules: LoanAmountFieldRules = {
    required: t('shared:personalInformation.inputs.loanAmount.requiredError'),
    min: {
      value: MIN_LOAN_AMOUNT,
      message: t('shared:personalInformation.inputs.loanAmount.rules.min', { amount: MIN_LOAN_AMOUNT }),
    },
    max: {
      value: MAX_LOAN_AMOUNT,
      message: t('shared:personalInformation.inputs.loanAmount.rules.max', { amount: MAX_LOAN_AMOUNT + 1 }),
    },
    onBlur: () => {
      const message = ltvHandler(allFields.propertyValue, allFields.loanAmount);
      submitCount === 0 && (message ? setError('loanAmount', { message }) : clearErrors('loanAmount'));
    },
    validate: {
      ltv: (v, formValues) => ltvHandler(formValues.propertyValue, v),
    },
    deps: 'propertyValue',
  };

  const secondaryApplicantInquiryRules = {
    required: t('shared:personalInformation.inputs.coApplicant.requiredError'),
  };

  const primaryApplicantIncomeRules: Omit<
    RegisterOptions,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  > = {
    required: 'Du måste ange inkomst innan du går vidare.',
  };

  const secondaryApplicantIncomeRules: Omit<
    RegisterOptions,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  > = {
    required: 'Du måste ange medsökandes inkomst innan du går vidare.',
  };

  const [isTracked, setIsTracked] = useState(false);
  const trackCalculatorStarted = () => {
    if (!isTracked) {
      trackCalculatorInit();
      setIsTracked(true);
    }
  };

  // focus on error functionality
  useEffect(() => {
    if (errors) {
      // errors need to be sorted according to keys in the form data type,
      // by default they are sorted in the order they were registered by RHF
      const order: Record<keyof FormDataType, number> = {
        propertyValue: 0,
        loanAmount: 1,
        secondaryApplicantExist: 2,
        primaryApplicantIncome: 3,
        secondaryApplicantIncome: 4,
        partnership: 5,
      };
      const firstError = Object.keys(errors).sort(
        (a, b) => order[a as keyof FormDataType] - order[b as keyof FormDataType],
      )[0];
      document.getElementsByName(firstError)[0]?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);

  useTrackCalculatorLoad('cost-calculator');

  return {
    control,
    handleSubmit,
    trackCalculatorStarted,
    reset,
    isDirty,
    propertyValueFieldRules,
    loanAmountFieldRules,
    secondaryApplicantInquiryRules,
    ltv,
    formFields: allFields,
    primaryApplicantIncomeRules,
    secondaryApplicantIncomeRules,
    dirtyFields,
  };
}
